/** @jsxImportSource @emotion/react */
import type { ReactNode } from 'react';
import { PanelCard } from '../../../design-system/molecules/PanelCard/PanelCard';
import { KeyValueList } from '../../../design-system/molecules/KeyValueList/KeyValueList';
import type { KeyValueItem } from '../../../design-system/molecules/KeyValueList/KeyValueList';
import { Button } from '../../../design-system/atoms/Button/Button';
import { Spinner } from '../../../design-system/atoms/Spinner/Spinner';
import { linkifyValue } from '../../utils/linkify';
import { relatedTableStyle, relatedMsgStyle, subtitleStyle, fkSectionStyle, fkLabelStyle, docCtaStyle } from './RowDetailPanel.styles';

export interface RowDetailPanelProps {
  /** 선택 행 식별 문구. 예: "posts · nid=101928…" (선택) */
  subtitle?: ReactNode;
  /** 선택 행의 모든 컬럼 key/value. 어떤 테이블이든 동일 구조. */
  entries: KeyValueItem[];
  /** 연관 행 이동 버튼 문구. 예: "comments 34건 보기 →" (선택) */
  fkLabel?: ReactNode;
  onOpenFk?: () => void;
  related?: { title: ReactNode; columns: string[]; rows: (string | number)[][] } | null;
  relatedLoading?: boolean;
  relatedError?: string | null;
  /** docparser 문서 행일 때만 노출되는 문서 뷰어 진입 버튼. */
  onOpenDoc?: () => void;
}

/** 행 상세 패널. 선택 행의 전체 컬럼과 FK 연관 행 이동에 활용. */
export function RowDetailPanel({
  subtitle,
  entries,
  fkLabel,
  onOpenFk,
  related,
  relatedLoading,
  relatedError,
  onOpenDoc,
}: RowDetailPanelProps) {
  return (
    <PanelCard
      title="행 상세"
      headerRight={subtitle !== undefined && <span css={subtitleStyle}>{subtitle}</span>}
    >
      {/* 문서 행(md_path 보유)일 때만 — 경로 문자열 대신 실제 문서를 열어 준다. */}
      {onOpenDoc && (
        <div css={docCtaStyle}>
          <Button onClick={onOpenDoc}>📄 문서 보기</Button>
        </div>
      )}
      <KeyValueList items={entries} />
      {fkLabel !== undefined && (
        <div css={fkSectionStyle}>
          <div css={fkLabelStyle}>연관 행 (FK)</div>
          <Button variant="text" onClick={onOpenFk}>
            {fkLabel}
          </Button>
          {relatedLoading && (
            <div css={relatedMsgStyle} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Spinner size={14} />
              <span>연관 행 로딩 중…</span>
            </div>
          )}
          {relatedError && <div css={relatedMsgStyle}>❌ {relatedError}</div>}
          {related && (
            <>
              <div css={fkLabelStyle}>{related.title}</div>
              {related.rows.length === 0 ? (
                <div css={relatedMsgStyle}>연관 행이 없습니다.</div>
              ) : (
                <table css={relatedTableStyle}>
                  <thead>
                    <tr>
                      {related.columns.map((c) => (
                        <th key={c}>{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {related.rows.map((row, i) => (
                      <tr key={i}>
                        {row.map((cell, j) => (
                          <td key={j}>{linkifyValue(cell)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
      )}
    </PanelCard>
  );
}
